import type { DailyUsageRow } from "../blob/format";
import { costForUsage } from "../cost";
import type { DailyPoint, DayToolValue, MonthlySeries, ToolId } from "../types";
import type { PriceLookup } from "../pricing-table";
import { TOOLS } from "../tools";

/**
 * The daily chart series, built from blob rows instead of a SQL group-by.
 *
 * Same output shape as `lib/history.ts` so the chart component is unchanged.
 * Isomorphic: this runs in the browser after decryption.
 */
export type { DayToolValue, DailyPoint, MonthlySeries };

/** Build the daily usage+cost series for a given YYYY-MM from merged blob rows. */
export function monthlySeriesFromRows(
  rows: DailyUsageRow[],
  month: string,
  lookup: PriceLookup,
): MonthlySeries {
  // Self-defensive: fall back to the current month for a malformed key.
  if (!/^\d{4}-\d{2}$/.test(month)) month = currentMonth();
  const [year, mon] = month.split("-").map(Number);

  // String-prefix matching, not date arithmetic. The `day` field was bucketed in
  // the exporting machine's local time; re-parsing it into a Date here would
  // reinterpret it in the *reader's* timezone and could shift a row into an
  // adjacent month when the two machines differ.
  const prefix = `${month}-`;

  const byDay = new Map<string, Record<ToolId, DayToolValue>>();
  for (const r of rows) {
    if (!r.day.startsWith(prefix)) continue;
    const day = byDay.get(r.day) ?? emptyDay();
    const v = day[r.tool];
    // A row for a tool the registry doesn't know (an older blob, a renamed id)
    // is skipped rather than crashing the chart on an undefined bucket.
    if (!v) continue;
    v.tokens +=
      r.inputTokens +
      r.outputTokens +
      r.cacheCreate5mTokens +
      r.cacheCreate1hTokens +
      r.cacheReadTokens;
    v.costUsd += costForUsage(lookup, r.model, r);
    byDay.set(r.day, day);
  }

  // Emit every calendar day in the month (zero-filled) for a continuous axis.
  const days: DailyPoint[] = [];
  const daysInMonth = new Date(year, mon, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${month}-${String(d).padStart(2, "0")}`;
    days.push({ day: key, perTool: byDay.get(key) ?? emptyDay() });
  }

  return { month, months: availableMonths(rows, month), days };
}

/** Distinct YYYY-MM months present in the rows, newest first. */
export function availableMonths(rows: DailyUsageRow[], include?: string): string[] {
  const months = new Set<string>();
  for (const r of rows) months.add(r.day.slice(0, 7));
  // The selected month may have no rows yet (e.g. the 1st of a new month); it
  // must still appear in the picker or the <select> shows a blank value.
  if (include) months.add(include);
  return [...months].sort().reverse();
}

function emptyDay(): Record<ToolId, DayToolValue> {
  const out = {} as Record<ToolId, DayToolValue>;
  for (const t of TOOLS) out[t.id] = { tokens: 0, costUsd: 0 };
  return out;
}

/** Current month key (YYYY-MM) in local time. */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
